import { stripeService } from '@/services/stripe';
import { ProviderEnum } from '@/types/common';
import {
  FinalizeStatus,
  type AdyenPaymentResponse,
  type NuveiPaymentResponse,
  type StripePaymentResponse,
} from '@/types/payment';
import type { RedirectPaymentContext } from '@/types/context';
import { CheckoutUrlPerEnvironment } from '@/constants/urls';
import { getEnvVariable } from '@/utils/get-env-variable';

export const getTabUrlPreparedForRedirect = (): string => {
  return `${CheckoutUrlPerEnvironment[getEnvVariable('VITE_ENV') as keyof typeof CheckoutUrlPerEnvironment]}/apm-dynamic-page`;
};

export const handleOpenTab = (url: string): Window | null => {
  return window.open(url, '_blank');
};

export const isRequireToOpenTab = (provider: ProviderEnum): boolean => {
  return provider === ProviderEnum.ADYEN || provider === ProviderEnum.NUVEI;
};

const getRedirectDataAdyen = async (
  paymentResponse: AdyenPaymentResponse
): Promise<Partial<RedirectPaymentContext>> => {
  const { adyen } = paymentResponse;
  const { status } = adyen;
  if (status === FinalizeStatus.REDIRECT) {
    return {
      redirectUrl: adyen.action?.url ?? undefined,
    };
  }
  return {};
};

const getRedirectDataNuvei = async (
  paymentResponse: NuveiPaymentResponse
): Promise<Partial<RedirectPaymentContext>> => {
  const { nuvei } = paymentResponse;
  const { redirectUrl, status } = nuvei;
  if (status === FinalizeStatus.REDIRECT) {
    return {
      redirectUrl: redirectUrl ?? undefined,
    };
  }
  return {};
};

const getRedirectDataStripe = async (
  paymentResponse: StripePaymentResponse
): Promise<Partial<RedirectPaymentContext>> => {
  const { stripe } = paymentResponse;
  const { clientSecret, status } = stripe;

  if (status === FinalizeStatus.REDIRECT) {
    const redirectUrl = await stripeService.getUrlFromSecretClientIntent(clientSecret);
    return {
      redirectUrl: redirectUrl ?? undefined,
      clientSecret,
      provider: ProviderEnum.STRIPE,
    };
  }

  return {};
};

export const getRedirectPaymentData = async (
  paymentResponse: unknown,
  provider: ProviderEnum
): Promise<Partial<RedirectPaymentContext>> => {
  if (provider === ProviderEnum.STRIPE) {
    return await getRedirectDataStripe(paymentResponse as StripePaymentResponse);
  }
  if (provider === ProviderEnum.ADYEN) {
    return await getRedirectDataAdyen(paymentResponse as AdyenPaymentResponse);
  }
  if (provider === ProviderEnum.NUVEI) {
    return await getRedirectDataNuvei(paymentResponse as NuveiPaymentResponse);
  }

  console.error(`Unsupported provider: ${provider}`);
  return {};
};
